import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./EmployeeTaskPage.css";

export default function EmployeeTaskPage() {
  const { id } = useParams();
  const [emp, setEmp] = useState({});
  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios
      .get(`${API_URL}/api/tasks/employee/${id}`)
      .then((res) => {
        setEmp(res.data.employee);
        setTasks(res.data.tasks);
      })
      .catch((err) => console.log(err));
  }, [id]);

  const filteredTasks = useMemo(() => {
    const q = search.toLowerCase();

    return tasks.filter((t) => {
      const title = t.title || t.taskName || "";
      const project = t.projectId?.projectName || t.projectName || "";

      return (
        title.toLowerCase().includes(q) ||
        project.toLowerCase().includes(q) ||
        (t.status ?? "").toLowerCase().includes(q) ||
        (t.priority ?? "").toLowerCase().includes(q)
      );
    });
  }, [tasks, search]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredTasks);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tasks");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(file, `${emp?.ename}_tasks.xlsx`);
  };

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => t.status === "Completed").length;
  const pendingTasks = tasks.filter((t) => t.status === "Pending").length;
  const inProgressTasks = tasks.filter((t) => t.status === "In Progress").length;

  return (
    <div className="emp-task-page">
      <div className="emp-task-shell">
        <div className="emp-task-hero">
          <div>
            <p className="emp-task-eyebrow">Employee Performance</p>
            <h3>{emp?.ename || "Employee"} Task List</h3>
            <span>Review workload, delivery status, and task priority trends</span>
          </div>

          <button className="emp-task-export-btn" onClick={exportToExcel}>
            Export Excel
          </button>
        </div>

        <div className="emp-task-kpi-grid">
          <div className="emp-task-kpi one">
            <p>Total Tasks</p>
            <h4>{totalTasks}</h4>
          </div>
          <div className="emp-task-kpi two">
            <p>Completed</p>
            <h4>{completedTasks}</h4>
          </div>
          <div className="emp-task-kpi three">
            <p>In Progress</p>
            <h4>{inProgressTasks}</h4>
          </div>
          <div className="emp-task-kpi four">
            <p>Pending</p>
            <h4>{pendingTasks}</h4>
          </div>
        </div>

        <div className="emp-task-search-wrap">
          <input
            type="text"
            className="emp-task-search"
            placeholder="Search by task, project, status, priority"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {search && (
            <button
              type="button"
              className="emp-task-clear-btn"
              onClick={() => setSearch("")}
            >
              Clear
            </button>
          )}
        </div>

        <div className="emp-task-table-card">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Project</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>View</th>
                </tr>
              </thead>

              <tbody>
                {filteredTasks.length > 0 ? (
                  filteredTasks.map((t) => {
                    const taskTitle = t.title || t.taskName || "-";
                    const projectTitle = t.projectId?.projectName || t.projectName || "-";
                    const statusClass = String(t.status || "").toLowerCase().replace(/\s+/g, "-");
                    const priorityClass = String(t.priority || "").toLowerCase();
                    const routeTaskId = t._id || t.taskId || t.id;

                    return (
                      <tr key={t._id || t.taskId}>
                        <td className="fw-semibold">{taskTitle}</td>
                        <td>{projectTitle}</td>
                        <td>
                          <span className={`emp-task-chip status-${statusClass}`}>{t.status || "-"}</span>
                        </td>
                        <td>
                          <span className={`emp-task-chip priority-${priorityClass}`}>{t.priority || "-"}</span>
                        </td>
                        <td>
                          {routeTaskId ? (
                            <Link
                              to={`/admin/TaskDetails/${routeTaskId}`}
                              className="emp-task-view-btn"
                            >
                              View Details
                            </Link>
                          ) : (
                            <button
                              type="button"
                              className="emp-task-view-btn"
                              onClick={() => alert("Task ID missing for this row.")}
                            >
                              View Details
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center emp-task-empty">
                      No tasks found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
